import EventType from './event-type'
import Organizer from './organizer'
import PACLocation from './pac-location'
import PACPerformance from './pac-performance'
import FAQ from './faq'
import Image from '../image'
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer'
import {
  getTwitterLink,
  getFacebookLink,
  getInstagramLink
} from '../../components/event/sidebar/organizer/social-links'
import { EventStatus } from './event-status'
import Ribbon from '../ribbon'
import BoxOfficeButtonAppearance from '../box-office-button-appearance'

export default class PACEvent {
  id: string
  slug: string
  name: string
  organizer?: Organizer
  ticketed: boolean
  boxOfficeUrl?: string
  boxOfficeButtonAppearance: BoxOfficeButtonAppearance
  programUrl?: string
  livestreamUrl?: string
  liveNow?: boolean
  embedLivestream?: boolean
  hasPassed?: boolean
  isToday?: boolean
  description?: any
  promotionImage: Image
  type: EventType
  location: PACLocation
  performances: PACPerformance[]
  faqs: FAQ[]
  dateRange?: string
  status: EventStatus
  statusAlertText: string
  ribbon: Ribbon

  constructor(values: any) {
    this.id = values.id
    this.slug = values.slug
    this.name = values.name
    this.organizer = values.organizer
    this.ticketed = values.ticketed
    this.boxOfficeUrl = values.boxOfficeUrl
    this.boxOfficeButtonAppearance = values.boxOfficeButtonAppearance
    this.programUrl = values.programUrl
    this.livestreamUrl = values.livestreamUrl
    this.liveNow = values.liveNow
    this.embedLivestream = values.embedLivestream
    this.hasPassed = values.hasPassed
    this.isToday = values.isToday
    this.description = values.description
    this.promotionImage = values.promotionImage
    this.type = values.type
    this.location = values.location
    this.performances = values.performances.map(performance => new PACPerformance(performance))
    this.faqs = values.faqs
    this.dateRange = values.dateRange
    this.status = EventStatus[values.status]
    this.statusAlertText = values.statusAlertText
    this.ribbon = values.ribbon
  }

  getPerformanceLabel() {
    let singularLabel

    switch (this.type) {
      case EventType.BAND_CONCERT:
      case EventType.CHOIR_CONCERT:
      case EventType.ORCHESTRA_CONCERT:
      case EventType.MUSIC_CONCERT:
        singularLabel = 'Concert'
        break
      case EventType.THEATRE_PERFORMANCE:
      case EventType.DANCE_PERFORMANCE:
        singularLabel = 'Performance'
        break
      case EventType.AWARDS_CEREMONY:
      case EventType.PRESENTATION:
        singularLabel = 'Presentation'
        break
      case EventType.ART_SHOW:
      case EventType.RECEPTION:
        singularLabel = 'Reception'
        break
      case EventType.BANQUET:
        singularLabel = 'Banquet'
        break
      case EventType.CHURCH_SERVICE:
        singularLabel = 'Service'
        break
      default:
        singularLabel = 'Performance'
    }

    if (this.performances.length !== 1) {
      return `${singularLabel}s`
    }

    return singularLabel
  }

  getJSONSchema() {
    const siteUrl = 'https://allenpac.org'

    let jsonEventStatus = 'https://schema.org/EventScheduled'
    switch (this.status) {
      case EventStatus.ACTIVE:
        jsonEventStatus = 'https://schema.org/EventScheduled'
        break
      case EventStatus.CANCELED:
        jsonEventStatus = 'https://schema.org/EventCancelled'
        break
      case EventStatus.POSTPONED:
        jsonEventStatus = 'https://schema.org/EventPostponed'
        break
    }

    const baseEvent: any = {
      '@type': 'Event',
      '@context': 'http://schema.org',
      name: this.name,
      description: documentToPlainTextString(this.description),
      image: `${this.promotionImage.url}`,
      url: `${siteUrl}/events/${this.slug}`,
      performer: {
        '@type': 'Organization',
        '@context': 'http://schema.org',
        name: this.organizer.name
      },
      organizer: {
        '@type': 'Organization',
        '@context': 'http://schema.org',
        name: this.organizer.name
      },
      eventStatus: jsonEventStatus
    }

    const physicalLocation = {
      '@context': 'http://schema.org',
      '@type': 'Place',
      name: this.location.name,
      address: this.location.address
    }

    if (this.livestreamUrl) {
      baseEvent.location = [
        {
          '@context': 'http://schema.org',
          '@type': 'VirtualLocation',
          url: this.livestreamUrl
        },
        physicalLocation
      ]
      baseEvent.eventAttendanceMode = 'https://schema.org/MixedEventAttendanceMode'
    } else {
      baseEvent.location = {
        '@context': 'http://schema.org',
        '@type': 'Place',
        name: this.location.name,
        address: this.location.address
      }
      baseEvent.eventAttendanceMode = 'https://schema.org/OfflineEventAttendanceMode'
    }

    const sameAs = []
    if (this.organizer.website) {
      sameAs.push(this.organizer.website)
      baseEvent.organizer.url = this.organizer.website
    }
    if (this.organizer.twitter) sameAs.push(getTwitterLink(this.organizer.twitter))
    if (this.organizer.facebook) sameAs.push(getFacebookLink(this.organizer.facebook))
    if (this.organizer.instagram) sameAs.push(getInstagramLink(this.organizer.instagram))

    if (sameAs.length > 0) {
      baseEvent.performer.sameAs = sameAs
      baseEvent.organizer.sameAs = sameAs
    }

    if (this.ticketed && this.boxOfficeUrl) {
      baseEvent['offers'] = {
        '@type': 'Offer',
        url: this.boxOfficeUrl
      }
    }

    const schemaEvents = this.performances.map(performance => {
      return {
        ...baseEvent,
        startDate: performance.startTime.seoFormat,
        endDate: performance.getEndDateString()
      }
    })

    return schemaEvents
  }
}
